/**
 * 零知识加密 v1 · 保险箱生命周期 + 条目加解密（高层封装）
 *
 * 依赖 crypto.ts 原语，密钥仅驻内存（VaultKeys），不落盘；
 * localStorage('lc-vault-kdf') 只缓存非机密的 kdfParams（salt + 包装后的 DEK 密文）。
 *
 * 契约要点（与 apps/api/src/vault/vault.service.ts、apps/api/test/vault.e2e-spec.ts 对齐）：
 * - init：    POST /vault/init   { authHash, recoveryVerifier?, kdfParams }
 * - unlock：  POST /vault/unlock { authHash }            → { kdfParams }（服务端只验 authHash）
 * - recover： POST /vault/recover { recoveryVerifier }   → { kdfParams }
 * - change：  POST /vault/change-password
 *             { currentAuthHash, authHash, recoveryVerifier?, kdfParams }
 *             recoveryVerifier 缺省=保留原值 / null=清除 / 字符串=更新（须带恢复密钥）
 *
 * 已知契约缺口（详见最终报告）：
 * 1) 后端无「预解锁取 kdfParams」接口 → salt/recoverySalt 只能靠本机缓存，
 *    新设备无法解锁/恢复（UI 需优雅提示 VaultKdfMissingError）。
 * 2) change-password 必须带 currentAuthHash（= 主密码派生）→ 忘记主密码时，
 *    恢复码只能解密数据急救，无法直接重置主密码。
 */
import {
  vaultChangePassword,
  vaultInit,
  vaultRecover,
  vaultUnlock,
} from './api';
import {
  DEFAULT_ARGON2,
  authHashOf,
  b64encode,
  decryptText,
  deriveKek,
  deriveSubKey,
  encryptText,
  randomBytes,
  recoveryVerifierOf,
  unwrapKeyBytes,
  wrapKeyBytes,
  type Argon2Params,
} from './crypto';
import type {
  KdfParamsJson,
  VaultChangePasswordBody,
  VaultInitBody,
  VaultItemData,
  VaultItemPayload,
  WrappedKeyJson,
} from './types';
import { STORAGE_KEYS } from './utils';

// ==================== 恢复码 ====================


/** 字符集 = 24 字母（去 I/O）+ 8 数字（2-9）= 32 个 → 字节 & 31 取模无偏差 */
export const RECOVERY_CHARSET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

/** 生成 24 位恢复码（6 组 × 4 字符，形如 XXXX-XXXX-XXXX-XXXX-XXXX-XXXX） */
export function generateRecoveryCode(): string {
  const bytes = randomBytes(24);
  let raw = '';
  for (const b of bytes) raw += RECOVERY_CHARSET[b & 31];
  return formatRecoveryCode(raw);
}

/** 原始 24 位 → 6 组 × 4 字符展示格式 */
export function formatRecoveryCode(raw: string): string {
  return (raw.toUpperCase().match(/.{1,4}/g) ?? []).join('-');
}

/** 规范化：大写 + 去所有分隔符/空白 */
export function normalizeRecoveryCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/** 规范化并校验（非法字符/长度直接抛中文错误，便于表单展示） */
export function assertRecoveryCode(input: string): string {
  const raw = normalizeRecoveryCode(input);
  const valid =
    raw.length === 24 && [...raw].every((ch) => RECOVERY_CHARSET.includes(ch));
  if (!valid) {
    throw new Error('恢复码格式不正确：应为 6 组 × 4 位（不含字母 I/O、数字 0/1）');
  }
  return raw;
}

// ==================== kdfParams 本机缓存 ====================

/** 本机缺少 kdfParams 缓存时抛出（UI 可识别并给「新设备」提示） */
export class VaultKdfMissingError extends Error {
  constructor(action: string) {
    super(
      `本机缺少保险箱解锁参数缓存（kdfParams），无法${action}。` +
        '请在曾经解锁过的设备上操作（后端未提供预解锁参数接口）。',
    );
    this.name = 'VaultKdfMissingError';
  }
}

export function loadCachedKdf(): KdfParamsJson | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.vaultKdf);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as KdfParamsJson;
    if (!parsed || typeof parsed !== 'object') return null;
    if (!parsed.salt || !parsed.wrappedVaultKey) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveCachedKdf(kdf: KdfParamsJson): void {
  localStorage.setItem(STORAGE_KEYS.vaultKdf, JSON.stringify(kdf));
}

export function clearCachedKdf(): void {
  localStorage.removeItem(STORAGE_KEYS.vaultKdf);
}

export function hasCachedKdf(): boolean {
  return loadCachedKdf() !== null;
}

function requireCachedKdf(action: string): KdfParamsJson {
  const kdf = loadCachedKdf();
  if (!kdf) throw new VaultKdfMissingError(action);
  return kdf;
}

/** kdfParams 里的 Argon2 参数（以服务端原样存储的为准） */
function kdfArgon2(kdf: KdfParamsJson): Argon2Params {
  return {
    iterations: kdf.iterations ?? DEFAULT_ARGON2.iterations,
    memorySize: kdf.memorySize ?? DEFAULT_ARGON2.memorySize,
    parallelism: kdf.parallelism ?? DEFAULT_ARGON2.parallelism,
    keyLength: kdf.keyLength ?? DEFAULT_ARGON2.keyLength,
  };
}

// ==================== 内存密钥 ====================

/** 解锁后驻内存的密钥（锁屏/登出必须 destroyKeys 清零） */
export interface VaultKeys {
  /** KEK：Argon2id(password, salt)，仅用于包装/解包 DEK */
  kek: Uint8Array;
  /** DEK：32B 随机主密钥（真实数据密钥的根） */
  dek: Uint8Array;
  /** HKDF(DEK, info="lightchaser-name")，加密条目名称 */
  nameKey: CryptoKey;
  /** HKDF(DEK, info="lightchaser-data")，加密条目数据 JSON */
  dataKey: CryptoKey;
}

async function deriveKeysFromDek(kek: Uint8Array, dek: Uint8Array): Promise<VaultKeys> {
  const [nameKey, dataKey] = await Promise.all([
    deriveSubKey(dek, 'lightchaser-name'),
    deriveSubKey(dek, 'lightchaser-data'),
  ]);
  return { kek, dek, nameKey, dataKey };
}

/** 清零字节密钥（CryptoKey 为不可导出，交由 GC） */
export function destroyKeys(keys: VaultKeys | null): void {
  if (!keys) return;
  keys.kek.fill(0);
  keys.dek.fill(0);
}

// ==================== 生命周期 ====================

export interface InitVaultResult {
  keys: VaultKeys;
  kdfParams: KdfParamsJson;
  hasRecovery: boolean;
  /** 恢复码明文：仅初始化/重置时返回一次，UI 必须一次性展示并提示抄写 */
  recoveryCode?: string;
}

export type RecoveryMode = 'keep' | 'clear' | 'reset';

export interface ChangeVaultPasswordResult {
  /** DEK 不变，条目密文无需重写 */
  keys: VaultKeys;
  kdfParams: KdfParamsJson;
  hasRecovery: boolean;
  /** recovery==='reset' 时的新恢复码明文（仅此一次） */
  recoveryCode?: string;
}

/**
 * 初始化保险箱：生成 salt/KEK/DEK，包装 DEK（+ 可选恢复码链路），
 * 提交 /vault/init 并缓存 kdfParams。
 */
export async function initVault(
  password: string,
  opts: { withRecovery?: boolean } = {},
): Promise<InitVaultResult> {
  const withRecovery = opts.withRecovery !== false;

  const salt = b64encode(randomBytes(32));
  const kek = await deriveKek(password, salt);
  const dek = randomBytes(32);
  const wrappedVaultKey = await wrapKeyBytes(kek, dek);

  const kdfParams: KdfParamsJson = {
    algorithm: 'argon2id',
    salt,
    iterations: DEFAULT_ARGON2.iterations,
    memorySize: DEFAULT_ARGON2.memorySize,
    parallelism: DEFAULT_ARGON2.parallelism,
    keyLength: DEFAULT_ARGON2.keyLength,
    wrappedVaultKey,
  };

  let recoveryCode: string | undefined;
  if (withRecovery) {
    recoveryCode = generateRecoveryCode();
    const recoverySalt = b64encode(randomBytes(32));
    const rkek = await deriveKek(normalizeRecoveryCode(recoveryCode), recoverySalt);
    kdfParams.recoverySalt = recoverySalt;
    kdfParams.wrappedVaultKeyRecovery = await wrapKeyBytes(rkek, dek);
    const recoveryVerifier = await recoveryVerifierOf(rkek);
    rkek.fill(0);

    const body: VaultInitBody = {
      authHash: await authHashOf(kek),
      recoveryVerifier,
      kdfParams,
    };
    await vaultInit(body);
  } else {
    await vaultInit({ authHash: await authHashOf(kek), kdfParams });
  }

  saveCachedKdf(kdfParams);
  return {
    keys: await deriveKeysFromDek(kek, dek),
    kdfParams,
    hasRecovery: withRecovery,
    recoveryCode,
  };
}

/**
 * 主密码解锁：KEK → authHash 送服务端校验 → 解包 DEK → 派生 nameKey/dataKey。
 * 服务端返回的 kdfParams 用于刷新本机缓存（保持与远端一致）。
 */
export async function unlockVault(password: string): Promise<VaultKeys> {
  const cached = requireCachedKdf('解锁');
  const kek = await deriveKek(password, cached.salt, kdfArgon2(cached));
  const res = await vaultUnlock({ authHash: await authHashOf(kek) });
  saveCachedKdf(res.kdfParams);
  const dek = await unwrapKeyBytes(kek, res.kdfParams.wrappedVaultKey);
  return deriveKeysFromDek(kek, dek);
}

/**
 * 恢复码解锁：RKEK = Argon2id(normalize(code), recoverySalt)，
 * recoveryVerifier 送服务端校验 → 用 RKEK 解包 DEK。
 * 注意：恢复码不解绑主密码（change-password 仍需 currentAuthHash）。
 */
export async function recoverVault(recoveryCodeInput: string): Promise<VaultKeys> {
  const cached = requireCachedKdf('使用恢复码');
  if (!cached.recoverySalt || !cached.wrappedVaultKeyRecovery) {
    throw new Error('本机缓存的解锁参数中没有恢复码信息，无法使用恢复码');
  }
  const raw = assertRecoveryCode(recoveryCodeInput);
  const rkek = await deriveKek(raw, cached.recoverySalt, kdfArgon2(cached));
  try {
    const res = await vaultRecover({ recoveryVerifier: await recoveryVerifierOf(rkek) });
    saveCachedKdf(res.kdfParams);
    const wrapped = res.kdfParams.wrappedVaultKeyRecovery ?? cached.wrappedVaultKeyRecovery;
    const dek = await unwrapKeyBytes(rkek, wrapped);
    return deriveKeysFromDek(rkek, dek);
  } finally {
    rkek.fill(0);
  }
}

/**
 * 修改主密码（DEK 不变，条目密文零重写）。
 * recovery 语义与后端一致：
 * - 'keep'：沿用原恢复码（kdfParams 带旧 recoverySalt/wrappedVaultKeyRecovery，省略 recoveryVerifier）
 * - 'clear'：清除恢复码（kdfParams 去恢复字段 + recoveryVerifier: null）
 * - 'reset'：重置恢复码（全新恢复链路 + recoveryVerifier 字符串），返回新恢复码明文
 */
export async function changeVaultPassword(
  currentPassword: string,
  newPassword: string,
  recovery: RecoveryMode = 'keep',
): Promise<ChangeVaultPasswordResult> {
  const oldKdf = requireCachedKdf('修改主密码');
  const oldParams = kdfArgon2(oldKdf);

  const currentKek = await deriveKek(currentPassword, oldKdf.salt, oldParams);
  const currentAuthHash = await authHashOf(currentKek);
  const dek = await unwrapKeyBytes(currentKek, oldKdf.wrappedVaultKey);
  currentKek.fill(0);

  const salt = b64encode(randomBytes(32));
  const kek = await deriveKek(newPassword, salt);
  const authHash = await authHashOf(kek);
  const wrappedVaultKey = await wrapKeyBytes(kek, dek);

  const kdfParams: KdfParamsJson = {
    algorithm: 'argon2id',
    salt,
    iterations: DEFAULT_ARGON2.iterations,
    memorySize: DEFAULT_ARGON2.memorySize,
    parallelism: DEFAULT_ARGON2.parallelism,
    keyLength: DEFAULT_ARGON2.keyLength,
    wrappedVaultKey,
  };

  const body: VaultChangePasswordBody = { currentAuthHash, authHash, kdfParams };
  let recoveryCode: string | undefined;

  if (recovery === 'reset') {
    recoveryCode = generateRecoveryCode();
    const recoverySalt = b64encode(randomBytes(32));
    const rkek = await deriveKek(normalizeRecoveryCode(recoveryCode), recoverySalt);
    kdfParams.recoverySalt = recoverySalt;
    kdfParams.wrappedVaultKeyRecovery = await wrapKeyBytes(rkek, dek);
    body.recoveryVerifier = await recoveryVerifierOf(rkek);
    rkek.fill(0);
  } else if (recovery === 'clear') {
    body.recoveryVerifier = null;
  } else if (oldKdf.recoverySalt && oldKdf.wrappedVaultKeyRecovery) {
    // 'keep' 且原有恢复码：原样沿用（DEK 不变 → 包装密文仍有效），省略 recoveryVerifier
    kdfParams.recoverySalt = oldKdf.recoverySalt;
    kdfParams.wrappedVaultKeyRecovery = oldKdf.wrappedVaultKeyRecovery;
  }
  // 'keep' 且原本无恢复码：kdfParams 不带恢复字段，省略 recoveryVerifier（保留 null）

  const res = await vaultChangePassword(body);
  saveCachedKdf(kdfParams);
  return {
    keys: await deriveKeysFromDek(kek, dek),
    kdfParams,
    hasRecovery: res.hasRecovery,
    recoveryCode,
  };
}

// ==================== 条目加解密 ====================

/** 与服务端 VaultItem 六密文字段一一对应（iv↔nonce） */
export interface VaultCipherFields {
  nameCipher: string;
  nameNonce: string;
  nameTag: string;
  dataCipher: string;
  dataNonce: string;
  dataTag: string;
}

/**
 * 加密条目：name 用 nameKey，data JSON 用 dataKey（AES-256-GCM）。
 * 返回值可直接展开进 Create/Update 请求体（不多一个字段）。
 */
export async function encryptItem(
  keys: VaultKeys,
  payload: VaultItemPayload,
): Promise<VaultCipherFields> {
  const nameW = await encryptText(keys.nameKey, payload.name);
  const dataW = await encryptText(keys.dataKey, JSON.stringify(payload.data));
  return {
    nameCipher: nameW.ct,
    nameNonce: nameW.iv,
    nameTag: nameW.tag,
    dataCipher: dataW.ct,
    dataNonce: dataW.iv,
    dataTag: dataW.tag,
  };
}

/** 解密条目（VaultItem 结构性满足 VaultCipherFields，可直接传服务端行） */
export async function decryptItem(
  keys: VaultKeys,
  fields: VaultCipherFields,
): Promise<VaultItemPayload> {
  const name = await decryptText(keys.nameKey, {
    iv: fields.nameNonce,
    ct: fields.nameCipher,
    tag: fields.nameTag,
  });
  const json = await decryptText(keys.dataKey, {
    iv: fields.dataNonce,
    ct: fields.dataCipher,
    tag: fields.dataTag,
  });
  return { name, data: JSON.parse(json) as VaultItemData };
}

/** 仅解密名称（列表页轻量展示，不触碰 dataKey） */
export async function decryptItemName(
  keys: VaultKeys,
  fields: VaultCipherFields,
): Promise<string> {
  return decryptText(keys.nameKey, {
    iv: fields.nameNonce,
    ct: fields.nameCipher,
    tag: fields.nameTag,
  });
}

/** WrappedKeyJson ↔ 六字段映射（历史记录行同样适用） */
export function wrappedFromFields(fields: VaultCipherFields, scope: 'name' | 'data'): WrappedKeyJson {
  return scope === 'name'
    ? { iv: fields.nameNonce, ct: fields.nameCipher, tag: fields.nameTag }
    : { iv: fields.dataNonce, ct: fields.dataCipher, tag: fields.dataTag };
}
